import ApiClient from 'api/ApiClient';
import { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';

import {
    EMAIL_UNSUBSCRIBE_CONFIRM_ERROR,
    EMAIL_UNSUBSCRIBE_CONFIRM_START,
    EMAIL_UNSUBSCRIBE_CONFIRM_SUCCESS,
    EMAIL_UNSUBSCRIBE_FETCH_INVALID,
    EMAIL_UNSUBSCRIBE_FETCH_START,
    EMAIL_UNSUBSCRIBE_FETCH_SUCCESS,
    EmailUnsubscribeAction
} from './EmailUnsubscribeActionTypes';

// ---------------------------------------------------------------------------
// URLs — same public-v1 base pattern used by PayByLink and UpdateExpiredVaccines.
// ---------------------------------------------------------------------------
const base_url = `${process.env.REACT_APP_HOST_URL}`;
const emailUnsubscribeUrl = base_url + '/api/public/v1/email_unsubscribes/';

// ---------------------------------------------------------------------------
// Backend response shapes — kept inline here because the endpoints are only
// consumed by this one thunk file (no shared surface needed).
// ---------------------------------------------------------------------------

interface EmailUnsubscribeInfoResponse {
    token: string;
    email: string;
    marketplace_name: string | null;
    already_opted_out: boolean;
}

// ---------------------------------------------------------------------------
// Sync action creators
// ---------------------------------------------------------------------------

export const fetchStart = (): EmailUnsubscribeAction => ({
    type: EMAIL_UNSUBSCRIBE_FETCH_START
});

export const fetchSuccess = (
    email: string,
    marketplaceName: string | null,
    alreadyOptedOut: boolean
): EmailUnsubscribeAction => ({
    type: EMAIL_UNSUBSCRIBE_FETCH_SUCCESS,
    payload: { email, marketplaceName, alreadyOptedOut }
});

export const fetchInvalid = (): EmailUnsubscribeAction => ({
    type: EMAIL_UNSUBSCRIBE_FETCH_INVALID
});

export const confirmStart = (): EmailUnsubscribeAction => ({
    type: EMAIL_UNSUBSCRIBE_CONFIRM_START
});

export const confirmSuccess = (): EmailUnsubscribeAction => ({
    type: EMAIL_UNSUBSCRIBE_CONFIRM_SUCCESS
});

export const confirmError = (): EmailUnsubscribeAction => ({
    type: EMAIL_UNSUBSCRIBE_CONFIRM_ERROR
});

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * GET /api/public/v1/email_unsubscribes/:token
 *
 * Fetches the info tied to a signed unsubscribe token. Any error (404 for a
 * bad token, network, 5xx) is collapsed into the `invalid` state — the user
 * doesn't need to disambiguate between "wrong token" and "server down".
 */
export const fetchEmailUnsubscribeInfoThunk = (
    token: string
): ThunkAction<void, RootState, null, EmailUnsubscribeAction> => {
    return async dispatch => {
        if (!token) {
            dispatch(fetchInvalid());
            return;
        }

        dispatch(fetchStart());

        try {
            const url = emailUnsubscribeUrl + encodeURIComponent(token);
            const res: AxiosResponse<EmailUnsubscribeInfoResponse> = await ApiClient.get(url);
            dispatch(fetchSuccess(res.data.email, res.data.marketplace_name, res.data.already_opted_out));
        } catch {
            dispatch(fetchInvalid());
        }
    };
};

/**
 * POST /api/public/v1/email_unsubscribes/:token
 *
 * Confirms the opt-out — the BE sets `email_optout = true` on the customer.
 * Idempotent server-side; on failure we roll back so the user can retry.
 */
export const confirmEmailUnsubscribeThunk = (
    token: string
): ThunkAction<void, RootState, null, EmailUnsubscribeAction> => {
    return async dispatch => {
        dispatch(confirmStart());

        try {
            const url = emailUnsubscribeUrl + encodeURIComponent(token);
            await ApiClient.post(url, null);
            dispatch(confirmSuccess());
        } catch {
            dispatch(confirmError());
        }
    };
};
